import {createTheme, Theme} from '@material-ui/core/styles';
import '@material-ui/core/styles';
import {PaletteType} from '@material-ui/core';
import {typography} from "./main.style";

declare module '@material-ui/core/styles/createPalette' {
  /* tslint:disable-next-line */
  interface CommonColors {
    indigo: string;
    yellow: string;
    violet: string;
    grey: string;
    greyLight: string;
    greyDark: string;
  }
}

/* MUI parses palette colors at theme-creation time (to compute light/dark
   shades, contrast, etc.) and rejects `var(--*)` strings. Use real hex
   values here. These must stay in sync with the CSS variables in
   src/index.css — change both when adjusting the palette. */

const lightPalette = {
  primary: '#5D395F',
  primaryLight: '#AE9FB0',
  secondary: '#DA6661',
  secondaryLight: '#EAB3B2',
  secondary700: '#F65A56',
  background: '#F6F4F7',
  paper: '#FFFFFF',
  black: '#000000',
  white: '#FFFFFF',
  indigo: '#6F4DB2',
  yellow: '#FFA800',
  violet: '#BF40B2',
  grey: '#7B7B83',
  greyLight: '#BDBDC1',
  greyDark: '#4A4A4E',
  green: '#2CB060',
  blue: '#1798E5',
  errorMain: '#E4655C',
  textPrimary: 'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(85, 85, 85, 1)',
  textDisabled: 'rgba(170, 170, 170, 1)',
  contrast: '#FFFFFF',
};

const darkPalette = {
  primary: '#5D395F',
  primaryLight: '#AE9FB0',
  secondary: '#F08A85',
  secondaryLight: '#4A2120',
  secondary700: '#FF8076',
  background: '#14101A',
  paper: '#323232',
  black: '#FFFFFF',
  white: '#323232',
  indigo: '#9F86D9',
  yellow: '#FFC04A',
  violet: '#DC72D0',
  grey: '#9A9AA2',
  greyLight: '#6B6B72',
  greyDark: '#C8C8CC',
  green: '#4CC97A',
  blue: '#4FB3F2',
  errorMain: '#FF8A82',
  textPrimary: 'rgba(220, 220, 224, 1)',
  textSecondary: 'rgba(195, 195, 200, 1)',
  textDisabled: 'rgba(120, 120, 128, 1)',
  /* contrastText for colored buttons (primary/secondary/error/etc) is
     always actual white — those buttons keep their brand color in both
     modes and need white text. Using c.white would produce dark text
     on a dark-purple button. */
  contrast: '#FFFFFF',
};

export const createAppTheme = (mode: PaletteType = 'light'): Theme => {
  const c = mode === 'dark' ? darkPalette : lightPalette;
  return createTheme({
    palette: {
      type: mode,
      primary: {
        main: c.primary,
        contrastText: c.contrast,
        light: c.primaryLight,
      },
      secondary: {
        main: c.secondary,
        contrastText: c.contrast,
        light: c.secondaryLight,
        700: c.secondary700,
      },
      common: {
        black: c.black,
        white: c.white,
        indigo: c.indigo,
        yellow: c.yellow,
        violet: c.violet,
        grey: c.grey,
        greyLight: c.greyLight,
        greyDark: c.greyDark,
      },
      background: {
        default: c.background,
        paper: c.paper,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textDisabled,
        hint: c.textDisabled,
      },
      error: {
        main: c.errorMain,
        contrastText: c.contrast,
      },
      success: {
        main: c.green,
        contrastText: c.contrast,
      },
      info: {
        main: c.blue,
        contrastText: c.contrast,
      },
      warning: {
        main: c.yellow,
        contrastText: c.contrast,
      },
    },
    typography: {
      fontFamily: [
        'Metropolis',
        'Helvetica',
        'Arial',
        'sans-serif'
      ].join(','),
      h1: {
        ...typography.heading1,
      },
      h2: {
        ...typography.heading2,
      },
      h3: {
        ...typography.heading3,
      },
      h4: {
        ...typography.heading4,
      },
      subtitle1: {
        ...typography.subtitle1,
      },
      subtitle2: {
        ...typography.feedback,
      },
      body1: {
        ...typography.body1,
      },
      body2: {
        ...typography.body1,
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 'normal',
      },
      button: {
        ...typography.button,
        textTransform: 'uppercase',
      },
    },
  });
};

const theme = createAppTheme('light');

export default theme;



// WEBPACK FOOTER //
// ./src/theme.ts